import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type ComponentType,
  type SVGProps,
} from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  History,
  LogOut,
  Menu,
  Package,
  ScrollText,
  Search,
  Settings,
  Shield,
  Workflow,
  X,
} from 'lucide-react';
import { MODULE_CATALOG, type AppModule } from '@vce/shared';
import { CommandPalette, commandShortcutLabel, type PaletteNavItem } from '@/components/command-palette';
import { QueueActionTip } from '@/components/queue-action-tip';
import { Badge, Select, Tooltip } from '@/components/ui/primitives';
import { api } from '@/lib/api';
import { cn, formatNumber } from '@/lib/utils';
import { useAuth } from './auth-context';
import { useTenant } from './tenant-context';

type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;
type NavArea = 'main' | 'system';

type NavLeaf = {
  to: string;
  label: string;
  icon: NavIcon;
  module: AppModule | null;
  area: NavArea;
  showReviewBadge?: boolean;
};

const SIDEBAR_KEY = 'vce.sidebarCollapsed';

const NAV_TREE: NavLeaf[] = [
  {
    to: MODULE_CATALOG.dashboard.path,
    label: MODULE_CATALOG.dashboard.label,
    icon: BarChart3,
    module: 'dashboard',
    area: 'main',
  },
  {
    to: MODULE_CATALOG.review.path,
    label: 'Fila',
    icon: ClipboardCheck,
    module: 'review',
    area: 'main',
    showReviewBadge: true,
  },
  {
    to: MODULE_CATALOG.products.path,
    label: MODULE_CATALOG.products.label,
    icon: Package,
    module: 'products',
    area: 'main',
  },
  {
    to: MODULE_CATALOG.runs.path,
    label: MODULE_CATALOG.runs.label,
    icon: Workflow,
    module: 'runs',
    area: 'main',
  },
  {
    to: MODULE_CATALOG.settings.path,
    label: MODULE_CATALOG.settings.label,
    icon: Settings,
    module: 'settings',
    area: 'system',
  },
  {
    to: '/admin',
    label: 'Admin',
    icon: Shield,
    module: null,
    area: 'system',
  },
];

function readCollapsedPreference(): boolean {
  const stored = window.localStorage.getItem(SIDEBAR_KEY);
  if (stored === '1') return true;
  if (stored === '0') return false;
  return window.matchMedia('(max-width: 1279px)').matches;
}

function formatBadgeCount(count: number): string {
  return count > 99 ? '99+' : String(count);
}

function reviewQueueDetail(products: number, suggestions: number): string {
  const productPart = products === 1 ? '1 produto' : `${formatNumber(products)} produtos`;
  const suggestionPart =
    suggestions === 1 ? '1 sugestão' : `${formatNumber(suggestions)} sugestões`;
  return `${MODULE_CATALOG.review.label} — ${productPart} · ${suggestionPart} pendentes`;
}

function leafVisible(
  leaf: NavLeaf,
  hasModule: (module: AppModule) => boolean,
  isOwner: boolean,
): boolean {
  if (leaf.module === null) return isOwner;
  return hasModule(leaf.module);
}

function filterNavTree(
  entries: NavLeaf[],
  hasModule: (module: AppModule) => boolean,
  isOwner: boolean,
): NavLeaf[] {
  return entries.filter((entry) => leafVisible(entry, hasModule, isOwner));
}

function flattenNavLeaves(entries: NavLeaf[]): PaletteNavItem[] {
  return entries.map((entry) => ({ to: entry.to, label: entry.label, icon: entry.icon }));
}

function SidebarNavLink({
  to,
  label,
  icon: Icon,
  badge,
  badgeDetail,
  collapsed,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: NavIcon;
  badge?: number;
  badgeDetail?: string;
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const hasBadge = badge !== undefined && badge > 0;
  const collapsedLabel = hasBadge ? (badgeDetail ?? `${label}, ${badge} pendentes`) : label;
  const badgeEl = hasBadge ? (
    <Badge tone="brand" className="tabular-nums">
      {formatBadgeCount(badge)}
    </Badge>
  ) : null;
  const link = (
    <NavLink
      to={to}
      aria-label={collapsed ? collapsedLabel : undefined}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'relative flex min-h-10 items-center rounded-[10px] text-sm font-medium transition',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
          collapsed ? 'mx-auto size-10 justify-center' : 'gap-2.5 px-3 py-2',
          isActive
            ? cn(
                'bg-[var(--color-surface-card)] text-[var(--color-ink)]',
                collapsed
                  ? null
                  : 'before:absolute before:inset-y-1.5 before:left-1 before:w-0.5 before:rounded-full before:bg-[var(--color-primary)]',
              )
            : 'text-[var(--color-body)] hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-ink)]',
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      {collapsed ? (
        hasBadge ? (
          <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-[var(--color-primary)]" aria-hidden />
        ) : null
      ) : (
        <>
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {hasBadge && badgeDetail ? (
            <Tooltip content={badgeDetail}>
              <span className="inline-flex">{badgeEl}</span>
            </Tooltip>
          ) : (
            badgeEl
          )}
        </>
      )}
    </NavLink>
  );

  if (!collapsed) return link;
  // Wrap in a span so Tooltip's asChild does not stringify NavLink's function className
  // (Radix Slot joins classNames with .join, which breaks size/centering utilities).
  return (
    <Tooltip content={hasBadge ? (badgeDetail ?? `${label} (${formatBadgeCount(badge)})`) : label} side="right">
      <span className="inline-flex">{link}</span>
    </Tooltip>
  );
}

export function AppLayout() {
  const { user, logout, hasModule } = useAuth();
  const { tenants, tenantId, tenant, select, isLoading } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const tenantSelectId = useId();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsedPreference);
  const [shortcut, setShortcut] = useState('Ctrl+K');
  const isOwner = user?.role === 'owner';
  const showTenantSelect = isOwner || tenants.length > 1;
  const canSearchProducts = hasModule('products');

  const visibleNav = useMemo(
    () => filterNavTree(NAV_TREE, hasModule, isOwner),
    [hasModule, isOwner],
  );
  const mainNav = visibleNav.filter((item) => item.area === 'main');
  const systemNav = visibleNav.filter((item) => item.area === 'system');
  const paletteNav = useMemo(() => {
    const items = flattenNavLeaves(visibleNav);
    if (hasModule('review')) {
      items.push({
        to: '/review?tab=history',
        label: 'Fila · Histórico',
        icon: ScrollText,
      });
    }
    if (hasModule('write_operations')) {
      items.push({
        to: `${MODULE_CATALOG.runs.path}?tab=publications`,
        label: 'Execuções · Publicações',
        icon: History,
      });
    }
    return items;
  }, [hasModule, visibleNav]);

  const reviewStats = useQuery({
    queryKey: ['suggestions', tenantId, 'stats'],
    queryFn: () => api.suggestions.stats(tenantId as string),
    enabled: tenantId !== null && hasModule('review'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const pendingReviewCount = reviewStats.data?.pendingProductCount ?? 0;
  const pendingSuggestionCount = reviewStats.data?.byStatus?.pending ?? 0;
  const reviewBadgeDetail =
    pendingReviewCount > 0 ? reviewQueueDetail(pendingReviewCount, pendingSuggestionCount) : undefined;

  useEffect(() => {
    setShortcut(commandShortcutLabel());
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const closeOnDesktop = (): void => {
      if (mq.matches) setMobileNavOpen(false);
    };
    closeOnDesktop();
    mq.addEventListener('change', closeOnDesktop);
    return () => mq.removeEventListener('change', closeOnDesktop);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      window.localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  const handleLogout = useCallback(() => {
    void logout().then(() => navigate('/login'));
  }, [logout, navigate]);

  const renderNavEntry = (entry: NavLeaf, isCollapsed: boolean) => (
    <SidebarNavLink
      key={entry.to}
      to={entry.to}
      label={entry.label}
      icon={entry.icon}
      badge={entry.showReviewBadge && pendingReviewCount > 0 ? pendingReviewCount : undefined}
      badgeDetail={entry.showReviewBadge ? reviewBadgeDetail : undefined}
      collapsed={isCollapsed}
      onNavigate={() => setMobileNavOpen(false)}
    />
  );

  const sidebar = (isCollapsed: boolean, showCollapseToggle: boolean) => (
    <>
      <div
        className={cn(
          'border-b border-[var(--color-hairline)]',
          isCollapsed ? 'flex justify-center px-0 py-4' : 'px-4 py-5',
        )}
      >
        {isCollapsed ? (
          <p className="font-display text-lg tracking-tight text-[var(--color-ink)]" aria-label="catalyze.ai">
            c
          </p>
        ) : (
          <>
            <p className="font-display text-lg tracking-tight text-[var(--color-ink)]">catalyze.ai</p>
            <p className="mt-0.5 text-xs text-[var(--color-muted)]">enriqueça, aprove, publique!</p>
          </>
        )}
      </div>

      <div
        className={cn(
          'border-b border-[var(--color-hairline)]',
          isCollapsed ? 'flex justify-center px-0 py-3' : 'px-4 py-3',
        )}
      >
        {isCollapsed ? (
          tenant ? (
            <Tooltip
              content={`${tenant.name}${tenant.isSandbox ? ' · sandbox' : ''}`}
              side="right"
            >
              <button
                type="button"
                onClick={toggleCollapsed}
                className="flex size-10 items-center justify-center rounded-[10px] bg-[var(--color-surface-card)] text-sm font-semibold text-[var(--color-ink)] hover:bg-[var(--color-surface-elevated)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]"
                aria-label={`Conta ${tenant.name}. Expandir menu para trocar.`}
              >
                {tenant.name.slice(0, 1).toUpperCase()}
              </button>
            </Tooltip>
          ) : null
        ) : (
          <>
            {showTenantSelect ? (
              <label
                htmlFor={tenantSelectId}
                className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[1.5px] text-[var(--color-muted)]"
              >
                Conta
              </label>
            ) : (
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[1.5px] text-[var(--color-muted)]">
                Conta
              </p>
            )}
            {isLoading && !tenant ? (
              <p className="text-sm text-[var(--color-muted)]">Carregando…</p>
            ) : showTenantSelect ? (
              <>
                <Select
                  id={tenantSelectId}
                  className="w-full"
                  value={tenantId ?? ''}
                  onChange={(event) => select(event.target.value)}
                >
                  {tenants.length === 0 ? <option value="">Nenhuma conta</option> : null}
                  {tenants.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
                {tenant && (tenant.isSandbox || !tenant.hasCredentials) ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {tenant.isSandbox ? <Badge tone="brand">sandbox</Badge> : null}
                    {!tenant.hasCredentials ? (
                      <Badge tone="critical">sem credencial</Badge>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : tenant ? (
              <div className="flex flex-wrap gap-1">
                <Badge tone="neutral">{tenant.name}</Badge>
                {tenant.isSandbox ? <Badge tone="brand">sandbox</Badge> : null}
                {!tenant.hasCredentials ? (
                  <Badge tone="critical">sem credencial</Badge>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-[var(--color-muted)]">Nenhuma conta</p>
            )}
          </>
        )}
      </div>

      <nav
        aria-label="Principal"
        className={cn(
          'min-h-0 flex-1 space-y-0.5 overflow-y-auto',
          isCollapsed ? 'flex flex-col items-center py-2' : 'p-2',
        )}
      >
        {mainNav.map((entry) => renderNavEntry(entry, isCollapsed))}
      </nav>

      {systemNav.length > 0 ? (
        <nav
          aria-label="Sistema"
          className={cn(
            'space-y-0.5 border-t border-[var(--color-hairline)]',
            isCollapsed ? 'flex flex-col items-center py-2' : 'p-2',
          )}
        >
          {systemNav.map((entry) => renderNavEntry(entry, isCollapsed))}
        </nav>
      ) : null}

      {showCollapseToggle ? (
        <div
          className={cn(
            'border-t border-[var(--color-hairline)]',
            isCollapsed ? 'flex justify-center py-2' : 'p-2',
          )}
        >
          <button
            type="button"
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
            onClick={toggleCollapsed}
            className={cn(
              'flex min-h-10 items-center rounded-[10px] text-sm font-medium text-[var(--color-body)]',
              'hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-ink)]',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
              isCollapsed ? 'size-10 justify-center' : 'w-full gap-2.5 px-3',
            )}
          >
            {isCollapsed ? (
              <ChevronRight className="size-4" aria-hidden />
            ) : (
              <>
                <ChevronLeft className="size-4 shrink-0" aria-hidden />
                Recolher
              </>
            )}
          </button>
        </div>
      ) : null}

      <div
        className={cn(
          'border-t border-[var(--color-hairline)]',
          isCollapsed ? 'flex justify-center py-2' : 'px-4 py-3',
        )}
      >
        {isCollapsed ? (
          <Tooltip content={user?.name ?? 'Sair'} side="right">
            <button
              type="button"
              aria-label="Sair"
              onClick={handleLogout}
              className={cn(
                'inline-flex size-10 items-center justify-center rounded-[10px]',
                'text-[var(--color-body)] hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-ink)]',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
              )}
            >
              <LogOut className="size-4" aria-hidden />
            </button>
          </Tooltip>
        ) : (
          <>
            <p className="truncate text-xs font-medium text-[var(--color-ink)]">{user?.name}</p>
            <p className="truncate text-[11px] text-[var(--color-muted)]">{user?.email}</p>
            <div className="mt-2 flex items-center justify-between gap-2">
              <Badge tone="neutral">{user?.role}</Badge>
              <button
                type="button"
                onClick={handleLogout}
                className={cn(
                  'inline-flex min-h-8 min-w-8 items-center gap-1.5 rounded-[10px] px-2 text-xs font-medium',
                  'text-[var(--color-body)] hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-ink)]',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
                )}
              >
                <LogOut className="size-3.5 shrink-0" aria-hidden />
                Sair
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );

  const searchButtonClass = cn(
    'inline-flex min-h-10 items-center gap-2 rounded-[10px] text-sm text-[var(--color-muted)]',
    'hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-ink)]',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
  );

  return (
    <div className="flex h-dvh overflow-hidden bg-[var(--color-canvas)]">
      <a
        href="#conteudo-principal"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-[10px] focus:bg-[var(--color-primary)] focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-[var(--color-on-primary)]"
      >
        Pular para o conteúdo
      </a>

      <aside
        aria-label="Navegação da aplicação"
        className={cn(
          'hidden h-full min-h-0 shrink-0 flex-col border-r border-[var(--color-hairline)] bg-[var(--color-surface-soft)] transition-[width] duration-200 md:flex',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        {sidebar(collapsed, true)}
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="z-20 flex items-center gap-3 border-b border-[var(--color-hairline)] bg-[var(--color-canvas)] px-4 py-3 md:hidden">
          <Dialog.Root open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <Dialog.Trigger asChild>
              <button
                type="button"
                aria-label="Abrir menu"
                className={cn(
                  'inline-flex min-h-10 min-w-10 items-center justify-center rounded-[10px] text-[var(--color-ink)]',
                  'hover:bg-[var(--color-surface-elevated)]',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
                )}
              >
                <Menu className="size-5" aria-hidden />
              </button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 md:hidden" />
              <Dialog.Content
                aria-describedby={undefined}
                className="fixed inset-y-0 left-0 z-50 flex h-full w-[min(100%,16rem)] flex-col border-r border-[var(--color-hairline)] bg-[var(--color-surface-soft)] outline-none md:hidden"
              >
                <div className="flex items-center justify-between border-b border-[var(--color-hairline)] px-4 py-3">
                  <Dialog.Title className="text-sm font-semibold text-[var(--color-ink)]">
                    Menu
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      aria-label="Fechar menu"
                      className={cn(
                        'inline-flex min-h-10 min-w-10 items-center justify-center rounded-[10px] text-[var(--color-muted)]',
                        'hover:bg-[var(--color-surface-elevated)] hover:text-[var(--color-ink)]',
                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
                      )}
                    >
                      <X className="size-5" aria-hidden />
                    </button>
                  </Dialog.Close>
                </div>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{sidebar(false, false)}</div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
          <div className="min-w-0 flex-1">
            <p className="font-display truncate text-sm tracking-tight text-[var(--color-ink)]">
              catalyze.ai
            </p>
            {tenant ? (
              <p className="truncate text-[11px] text-[var(--color-muted)]">{tenant.name}</p>
            ) : null}
          </div>
          <button
            type="button"
            aria-label={`Buscar (${shortcut})`}
            onClick={() => setPaletteOpen(true)}
            className={cn(searchButtonClass, 'min-w-10 justify-center px-0')}
          >
            <Search className="size-5" aria-hidden />
          </button>
        </div>

        <div className="z-20 hidden items-center justify-end border-b border-[var(--color-hairline)] bg-[var(--color-canvas)] px-6 py-3 md:flex">
          <button
            type="button"
            aria-label={`Buscar (${shortcut})`}
            onClick={() => setPaletteOpen(true)}
            className={cn(searchButtonClass, 'border border-[var(--color-hairline)] px-3')}
          >
            <Search className="size-4 shrink-0" aria-hidden />
            <span>Buscar</span>
            <kbd className="rounded border border-[var(--color-hairline)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-muted)]">
              {shortcut}
            </kbd>
          </button>
        </div>

        <main
          id="conteudo-principal"
          tabIndex={-1}
          className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto outline-none"
        >
          <Outlet />
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        navItems={paletteNav}
        tenants={tenants}
        tenantId={tenantId}
        canSearchProducts={canSearchProducts}
        onNavigate={(to) => navigate(to)}
        onSelectTenant={select}
        onLogout={handleLogout}
      />

      <QueueActionTip />
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
}) {
  return (
    <header className="flex flex-col gap-3 border-b border-[var(--color-hairline)] bg-[var(--color-canvas)] px-4 py-5 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:px-6">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight text-[var(--color-ink)]">{title}</h1>
        {description ? (
          <p className="mt-1 text-sm text-[var(--color-muted)]">{description}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">{actions}</div>
      ) : null}
    </header>
  );
}
